import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Reply, Send, User, Search, ChevronLeft, ChevronRight, X, Eye, Inbox, Loader2, Mail, Info } from 'lucide-react';

const API_URL = '/api';

const ReplyToEmails = () => {
  const [stats, setStats] = useState({ received: 0, sent: 0 });
  const [receivedEmails, setReceivedEmails] = useState([]);
  const [replyPreview, setReplyPreview] = useState({ subject: '', body: '', recipient: '', originalEmailId: null });
  const [messagePreview, setMessagePreview] = useState('');
  const [showReplySection, setShowReplySection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmailForPreview, setSelectedEmailForPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [isGmailConnected, setIsGmailConnected] = useState(false);
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    loadStats();
    const savedApiKey = localStorage.getItem('apiKey');
    if (savedApiKey) setApiKey(savedApiKey);
    
    const savedGmail = localStorage.getItem('gmailEmail');
    setIsGmailConnected(!!savedGmail);
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/stats`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadReceivedEmails = async (page = 1) => {
    try {
      setLoading(true);
      setLoadingText('📧 Loading received emails...');
      
      const response = await axios.get(`${API_URL}/replies/received-emails?page=${page}&per_page=20`);
      
      if (response.data.success) {
        setReceivedEmails(response.data.emails);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.total_pages);
        setShowReplySection(true);
      }
    } catch (error) {
      alert(`❌ Failed to load emails!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadReceivedEmails(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const filteredEmails = receivedEmails.filter(email => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      email.from_name.toLowerCase().includes(query) ||
      email.from_email.toLowerCase().includes(query) ||
      email.subject.toLowerCase().includes(query)
    );
  });

  const generateReplyForEmail = async (emailId) => {
    if (!apiKey) {
      alert('Please configure your AI API key in Settings!');
      return;
    }

    setLoading(true);
    setLoadingText('🤖 Generating AI reply...');

    try {
      const response = await axios.post(`${API_URL}/replies/generate`, {
        emailId: emailId,
        apiKey: apiKey,
        sessionId: 'react-session'
      });

      if (response.data.success) {
        setReplyPreview({
          subject: response.data.subject,
          body: response.data.body,
          recipient: response.data.recipient_email,
          originalEmailId: response.data.original_email_id
        });
        
        setMessagePreview(`To: ${response.data.recipient_email}\nSubject: ${response.data.subject}\n\n${response.data.body}`);
        
        alert(`✅ Reply Generated!\n\nRecipient: ${response.data.recipient_email}\n\nReview and click "Create Reply Draft" to save!`);
      }
    } catch (error) {
      alert(`❌ Failed to generate reply!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createReplyDraft = async () => {
    if (!isGmailConnected) {
      alert('Please connect Gmail in Settings first!');
      return;
    }

    if (!replyPreview.recipient || !replyPreview.subject || !replyPreview.body) {
      alert('Please generate a reply first!');
      return;
    }

    setLoading(true);
    setLoadingText('📧 Creating reply draft...');

    try {
      const response = await axios.post(`${API_URL}/replies/create-draft`, {
        sessionId: 'react-session',
        recipientEmail: replyPreview.recipient,
        subject: replyPreview.subject,
        body: replyPreview.body,
        originalEmailId: replyPreview.originalEmailId
      });

      if (response.data.success) {
        loadStats();
        alert(`✅ Reply Draft Created!\n\nSaved to Gmail Drafts for: ${replyPreview.recipient}\n\nCheck your Gmail Drafts folder!`);
        setMessagePreview('');
        setReplyPreview({ subject: '', body: '', recipient: '', originalEmailId: null });
      }
    } catch (error) {
      alert(`❌ Failed to create draft!\n\n${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {loading && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
          <Loader2 className="h-12 w-12 animate-spin text-white" />
          <p className="mt-4 text-xl font-semibold text-white">{loadingText}</p>
        </div>
      )}

      <Header 
        title="Reply to Emails" 
        subtitle="Generate AI replies to received emails"
        stats={stats}
      />

      <div className="p-8 max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Main Card */}
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Reply className="h-6 w-6 text-cyan-600" />
                Reply to Received Emails
              </CardTitle>
              <CardDescription className="flex items-center gap-2">
                <Inbox className="h-4 w-4" />
                Total: {stats.received.toLocaleString()} received emails in database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Load Emails Button */}
              {!showReplySection && (
                <div className="text-center py-8">
                  <Button
                    onClick={loadReceivedEmails}
                    className="h-16 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-lg px-12"
                  >
                    <Inbox className="mr-2 h-6 w-6" />
                    Load Received Emails ({stats.received.toLocaleString()})
                  </Button>
                  <p className="text-sm text-gray-500 mt-4">
                    Click to load emails from database and start generating replies
                  </p>
                </div>
              )}

              {/* Emails List */}
              {showReplySection && receivedEmails.length > 0 && (
                <div className="space-y-4">
                  {/* Header with Search */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search emails by name, address, or subject..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 pr-10"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                        </button>
                      )}
                    </div>
                    <Button
                      onClick={() => {
                        setShowReplySection(false);
                        setSearchQuery('');
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <X className="mr-1 h-4 w-4" />
                      Hide List
                    </Button>
                  </div>

                  {/* Stats Bar */}
                  <div className="flex items-center justify-between px-3 py-2 bg-cyan-50 rounded-lg">
                    <div className="text-sm text-gray-700">
                      Showing {filteredEmails.length} of {receivedEmails.length} emails (Page {currentPage} of {totalPages})
                    </div>
                    {searchQuery && (
                      <Badge variant="secondary" className="text-xs">
                        <Search className="mr-1 h-3 w-3" />
                        "{searchQuery}"
                      </Badge>
                    )}
                  </div>
                  
                  {/* Email Cards */}
                  <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2">
                    {filteredEmails.length > 0 ? (
                      filteredEmails.map((email) => (
                        <div
                          key={email.id}
                          className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-all border border-cyan-100 hover:border-cyan-300"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <User className="h-4 w-4 text-cyan-600 flex-shrink-0" />
                                <div className="font-semibold text-gray-900 truncate">
                                  {email.from_name}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  #{email.id}
                                </Badge>
                              </div>
                              <div className="text-sm text-cyan-600 truncate mb-2 font-medium">
                                📧 {email.from_email}
                              </div>
                              <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Mail className="h-3 w-3 text-gray-400" />
                                {email.subject}
                              </div>
                              <div className="text-sm text-gray-500 line-clamp-2 mb-3">
                                {email.body_preview}
                              </div>
                              <div className="flex items-center gap-3 text-xs text-gray-400">
                                <span>📅 {new Date(email.date).toLocaleDateString()}</span>
                                <span>•</span>
                                <span>🕒 {new Date(email.date).toLocaleTimeString()}</span>
                              </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 flex-shrink-0">
                              <Button
                                onClick={() => generateReplyForEmail(email.id)}
                                size="sm"
                                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                              >
                                <Reply className="mr-1 h-4 w-4" />
                                Reply
                              </Button>
                              <Button
                                onClick={() => setSelectedEmailForPreview(email)}
                                size="sm"
                                variant="outline"
                                className="border-cyan-300"
                              >
                                <Eye className="mr-1 h-4 w-4" />
                                View
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <Search className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                        <p>No emails found matching "{searchQuery}"</p>
                        <Button
                          onClick={() => setSearchQuery('')}
                          variant="ghost"
                          size="sm"
                          className="mt-3"
                        >
                          Clear Search
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-cyan-100">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="outline"
                      >
                        <ChevronLeft className="mr-1 h-4 w-4" />
                        Previous
                      </Button>
                      
                      <div className="flex items-center gap-2">
                        {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = idx + 1;
                          } else if (currentPage <= 3) {
                            pageNum = idx + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + idx;
                          } else {
                            pageNum = currentPage - 2 + idx;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              variant={currentPage === pageNum ? "default" : "ghost"}
                              size="sm"
                              className={currentPage === pageNum ? "bg-cyan-600 hover:bg-cyan-700" : ""}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        variant="outline"
                      >
                        Next
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Info */}
              <div className="bg-cyan-50 rounded-lg p-4 text-sm text-gray-700">
                <div className="font-semibold mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4 text-cyan-600" />
                  How it works:
                </div>
                <ul className="space-y-1 text-xs ml-6">
                  <li>• Load emails from your database</li>
                  <li>• Click "Reply" on any email</li>
                  <li>• AI generates professional reply</li>
                  <li>• Review the generated content</li>
                  <li>• Save as draft with recipient pre-filled</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          {messagePreview && (
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-cyan-600" />
                  Reply Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  value={messagePreview}
                  readOnly
                  rows="12"
                  className="font-mono text-sm resize-none"
                />
                <Button
                  onClick={createReplyDraft}
                  className="w-full h-14 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-lg"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Create Reply Draft
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Email Preview Modal */}
      {selectedEmailForPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Email Preview
                </CardTitle>
                <Button
                  onClick={() => setSelectedEmailForPreview(null)}
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <User className="h-4 w-4 text-cyan-600" />
                    From
                  </Label>
                  <div className="mt-1 p-3 bg-cyan-50 rounded-lg">
                    <div className="font-medium text-gray-900">{selectedEmailForPreview.from_name}</div>
                    <div className="text-sm text-cyan-600">{selectedEmailForPreview.from_email}</div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-cyan-600" />
                    Subject
                  </Label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-lg font-medium text-gray-900">
                    {selectedEmailForPreview.subject}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-700">Date</Label>
                  <div className="mt-1 text-sm text-gray-600">
                    📅 {new Date(selectedEmailForPreview.date).toLocaleDateString()} at {new Date(selectedEmailForPreview.date).toLocaleTimeString()}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-gray-700">Message</Label>
                  <div className="mt-1 p-4 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedEmailForPreview.body_preview}
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => {
                      generateReplyForEmail(selectedEmailForPreview.id);
                      setSelectedEmailForPreview(null);
                    }}
                    className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                  >
                    <Reply className="mr-2 h-4 w-4" />
                    Generate Reply
                  </Button>
                  <Button
                    onClick={() => setSelectedEmailForPreview(null)}
                    variant="outline"
                    className="flex-1"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReplyToEmails;

